import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';

type TagType = 'feature' | 'improvement' | 'fix';

interface ChangelogEntry {
  version: string;
  date: string;
  title?: string;
  changes: { tag: TagType; text: string }[];
}

const tagColors: Record<TagType, string> = {
  feature: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  improvement: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  fix: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
};

// Release-note content stays in English: refers to specific code paths,
// branded feature names, and historical changes that get awkward when
// translated. The page chrome (heading, tag labels) is i18n'd above.
const entries: ChangelogEntry[] = [
  {
    version: 'v0.10.0',
    date: 'April 2026',
    title: 'AI Investigator, v2',
    changes: [
      { tag: 'feature', text: 'Byte — rebranded AI assistant (formerly Copilot) integrated in dashboard and header' },
      { tag: 'feature', text: 'Workspace knowledge tools: investigator now draws on saved data models, services, and parser rules' },
      { tag: 'feature', text: 'Proactive get_system_health_snapshot tool for single-pass health checks' },
      { tag: 'feature', text: 'Scope checks and honest insight-mode reporting in AI investigations' },
      { tag: 'feature', text: 'update_panel / remove_panel events on the investigation canvas' },
      { tag: 'improvement', text: 'isolated-vm sandboxing for safer pipeline execution' },
      { tag: 'improvement', text: 'Pulse animation on PulseCanvas for live log visualisation' },
      { tag: 'improvement', text: 'LogTable events mode with richer parsed-field and attribute rendering' },
      { tag: 'improvement', text: 'Stats queries and visualizations overhaul for faster dashboards' },
      { tag: 'fix', text: 'IPv4-mapped IPv6 handling in BLOCKED_IP_RANGES' },
    ],
  },
  {
    version: 'v0.9.0',
    date: 'March 2026',
    title: 'Early Access',
    changes: [
      { tag: 'feature', text: 'Visual Pipeline editor with drag-and-drop node library' },
      { tag: 'feature', text: 'Kubernetes integration via Helm chart' },
      { tag: 'improvement', text: 'LPQL syntax highlighting in docs' },
      { tag: 'feature', text: 'Vector agent support' },
    ],
  },
  {
    version: 'v0.8.0',
    date: 'February 2026',
    changes: [
      { tag: 'feature', text: 'AI Anomaly Detection with Modified Z-Score algorithm' },
      { tag: 'feature', text: 'Cloud integrations (Azure DevOps, GitHub Actions, Railway)' },
      { tag: 'feature', text: 'HTTP API with batch ingestion' },
      { tag: 'improvement', text: 'Custom dashboard widgets' },
    ],
  },
  {
    version: 'v0.7.0',
    date: 'January 2026',
    changes: [
      { tag: 'feature', text: 'LPQL query language with Splunk-familiar pipe commands' },
      { tag: 'improvement', text: 'ClickHouse-powered search (<200ms P95)' },
      { tag: 'feature', text: 'Multi-tenant architecture' },
      { tag: 'feature', text: 'API key management' },
    ],
  },
  {
    version: 'v0.6.0',
    date: 'December 2025',
    title: 'Alpha',
    changes: [
      { tag: 'feature', text: 'Core log ingestion pipeline' },
      { tag: 'feature', text: 'Basic search & filtering' },
      { tag: 'feature', text: 'User authentication' },
      { tag: 'feature', text: 'Initial dashboard' },
    ],
  },
];

export function ChangelogPage() {
  const t = useT();

  useDocumentMeta({
    title: t('changelog.meta.title'),
    description: t('changelog.meta.description'),
  });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('changelog.hero.title')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('changelog.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gray-200 dark:bg-gray-800" />

          <div className="space-y-12">
            {entries.map((entry) => (
              <div key={entry.version} className="relative pl-10">
                {/* Dot */}
                <div className="absolute left-0 top-1.5 h-[18px] w-[18px] rounded-full border-2 border-primary-500 bg-white dark:bg-gray-950" />

                {/* Header */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary-500/10 text-primary-400 border border-primary-500/20">
                    {entry.version}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{entry.date}</span>
                  {entry.title && (
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {entry.title}
                    </span>
                  )}
                </div>

                {/* Changes */}
                <ul className="space-y-3">
                  {entry.changes.map((change, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className={`inline-flex shrink-0 px-2 py-0.5 rounded text-[11px] font-medium mt-0.5 ${tagColors[change.tag]}`}
                      >
                        {t(`changelog.tag.${change.tag}`)}
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        {change.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
